import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { es, enUS, Locale } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAtom } from "jotai";
import { languageAtom } from "@/store/language";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";

interface DatePickerProps {
  date?: Date | null;
  setDate: (date: Date | null) => void;
  disableFutureDates?: boolean;
}

export function DatePicker({
  date,
  setDate,
  disableFutureDates = false,
}: DatePickerProps) {
  const { t } = useTranslation();
  // Get the current language from the language atom
  const [language] = useAtom(languageAtom);

  // Map language code to date-fns locale
  const localeMap: Record<string, Locale> = {
    en: enUS,
    es: es,
  };

  // Use the appropriate locale based on the selected language
  const locale = localeMap[language || "en"];

  // Get current date for defaults
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  // State for selected year and month
  const [selectedYear, setSelectedYear] = useState<number>(
    date ? date.getFullYear() : currentYear,
  );
  const [selectedMonth, setSelectedMonth] = useState<number>(
    date ? date.getMonth() : currentMonth,
  );

  // Update selected year and month when date prop changes
  useEffect(() => {
    if (date) {
      setSelectedYear(date.getFullYear());
      setSelectedMonth(date.getMonth());
    } else {
      setSelectedYear(currentYear);
      setSelectedMonth(currentMonth);
    }
  }, [date, currentYear, currentMonth]);

  // Generate year options (10 years before and after current year)
  const yearOptions = Array.from(
    { length: 21 },
    (_, i) => currentYear - 10 + i,
  );

  // Month options with translations
  const monthOptions = [
    { value: 0, label: t("months.january", "January") },
    { value: 1, label: t("months.february", "February") },
    { value: 2, label: t("months.march", "March") },
    { value: 3, label: t("months.april", "April") },
    { value: 4, label: t("months.may", "May") },
    { value: 5, label: t("months.june", "June") },
    { value: 6, label: t("months.july", "July") },
    { value: 7, label: t("months.august", "August") },
    { value: 8, label: t("months.september", "September") },
    { value: 9, label: t("months.october", "October") },
    { value: 10, label: t("months.november", "November") },
    { value: 11, label: t("months.december", "December") },
  ];

  // Function to handle date selection and ensure correct date
  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) {
      setDate(null);
      return;
    }

    // Create a new date object with the selected date to avoid timezone issues
    const correctedDate = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
    );
    setDate(correctedDate);
  };

  // Function to handle year change
  const handleYearChange = (year: string) => {
    const newYear = parseInt(year);
    setSelectedYear(newYear);
    // If there's a selected date, update it with the new year
    if (date) {
      const newDate = new Date(newYear, date.getMonth(), date.getDate());
      setDate(newDate);
    }
  };

  // Function to handle month change
  const handleMonthChange = (month: string) => {
    const newMonth = parseInt(month);
    setSelectedMonth(newMonth);
    // If there's a selected date, update it with the new month
    if (date) {
      const newDate = new Date(date.getFullYear(), newMonth, date.getDate());
      setDate(newDate);
    }
  };

  // Function to disable dates based on props
  const disableDate = (date: Date) => {
    if (disableFutureDates) {
      // Deshabilitar fechas futuras (posteriores a hoy)
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
      return date > today;
    }
    return false;
  };

  // Create the month to display in the calendar based on selected year and month
  const displayMonth = new Date(selectedYear, selectedMonth, 1);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className={cn(
            "w-full justify-start text-left font-normal",
            !date && "text-muted-foreground",
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? (
            format(date, "PPP", { locale })
          ) : (
            <span>{t("common.pickDate", "Pick a date")}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex items-center justify-between p-3 border-b">
          <Select
            value={selectedMonth.toString()}
            onValueChange={handleMonthChange}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((month) => (
                <SelectItem key={month.value} value={month.value.toString()}>
                  {month.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={selectedYear.toString()}
            onValueChange={handleYearChange}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Calendar
          mode="single"
          selected={date || undefined}
          onSelect={handleDateSelect}
          disabled={disableDate}
          initialFocus
          month={displayMonth}
          onMonthChange={(newMonth) => {
            setSelectedYear(newMonth.getFullYear());
            setSelectedMonth(newMonth.getMonth());
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
